import type { IResultStamp } from "@/api/AppApi/common-interfaces/IResultStamp";
import type { Config } from "@/api/AppApi/config";
import type {
  IGetCart,
  ISubmitCheckoutResponse,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import ApiService from "@/api/baseApi/api-service";
import type {
  ICheckToken,
  IGetAllOrders,
  IGetAllOrdersParams,
  IGetOrderDetails,
  IGetPaymentLink,
  IGetReturnedInvoices,
  IGetReturnedInvoiceDetails,
  IGetExchangeInvoices,
  IGetExchangeInvoiceDetails,
} from "./interfaces/order-interfaces";

const URLs = {
  getAllOrders: "/orders",
  getOrderDetails: "/orders/",
  checkToken: "check-token",
};
class ApiOrder extends ApiService {
  constructor(config: Config) {
    super({ ...config });
  }
  getAllOrders = (params: IGetAllOrdersParams): Promise<IGetAllOrders> =>
    this.get(URLs.getAllOrders, { params });
  getPopupOrderCount = async (): Promise<number> => {
    // No status/date filters: cancelled and refunded orders still count.
    const response = await this.get<IGetAllOrders>(URLs.getAllOrders, {
      params: { limit: 1, page: 1 },
      timeout: 5000,
      // Eligibility is store-wide, independent of the selected pickup branch.
      headers: { "Branch-Id": undefined },
    });
    const total = response.result?.meta?.total;
    if (!Number.isInteger(total) || total < 0) {
      throw new Error("Missing placed-order count");
    }
    return total;
  };
  getOrderDetails = (id: number): Promise<IGetOrderDetails> =>
    this.get(URLs.getOrderDetails + id, {});
  reorder = (id: number): Promise<IGetCart> =>
    this.post(`orders/${id}/reorder`, {});
  cancelOrder = (id: number): Promise<IGetCart> =>
    this.post(`orders/${id}/cancel`, {});
  getPaymentLink = (id: number): Promise<IGetPaymentLink> =>
    this.get(`orders/${id}/payment-link`, {});
  pay = ({
    order_id,
    gateway_id,
    gateway_account_id,
  }: {
    order_id: number;
    gateway_id: number;
    gateway_account_id: number;
  }): Promise<ISubmitCheckoutResponse> =>
    this.post(`orders/${order_id}/pay`, { gateway_id, gateway_account_id });
  checkToken = (token: string): Promise<ICheckToken> =>
    this.get(URLs.checkToken, { params: { token } });
  confirmCod = ({
    order_id,
    status,
  }: {
    order_id: number;
    status: "confirmed" | "canceled";
  }): Promise<IResultStamp> =>
    this.post(`orders/${order_id}/confirm-cod`, { status });
  refundOrder = ({
    orderId,
    items,
  }: {
    orderId: number;
    items: { id: number; quantity: number; note: string }[];
  }): Promise<IResultStamp> =>
    this.post(`returned-invoices`, { invoice_id: orderId, items: items });

  getReturnedInvoices = (orderId: number): Promise<IGetReturnedInvoices> =>
    this.get(`returned-invoices`, {
      params: { invoice_id: orderId, limit: 100 },
    });

  getReturnedInvoiceDetails = (
    id: number,
  ): Promise<IGetReturnedInvoiceDetails> =>
    this.get(`returned-invoices/${id}`, {});

  
  getExchangeInvoices = (orderId: number): Promise<IGetExchangeInvoices> =>
    this.get(`exchange-invoices`, {
      params: { invoice_id: orderId, limit: 100 },
    });

  getExchangeInvoiceDetails = (
    id: number,
  ): Promise<IGetExchangeInvoiceDetails> =>
    this.get(`exchange-invoices/${id}`, {});
}
export default ApiOrder;

